// @flow
import gql from 'graphql-tag';
import * as React from 'react';
import { graphql } from 'react-apollo';
import Link from 'components/Link';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import UserActions from 'user/UserActions';
import BoxBody from 'components/BoxBody';
import Buttons from 'components/Buttons';
import BoxHeader from 'components/BoxHeader';
import LinkConstants from 'constants/LinkConstants';
import LoadingIndicator from 'components/LoadingIndicator';

import './styles/PIIConsentModal.scss';

type Props = {
  _recordPrompt: Function,
  _recordAcceptance: Function,

  onConsentGiven: Function,
  onConsentRefused: Function,
  onConsentGivenError: Function,
  onConsentRefusedError: Function,
};

type State = {
  working: boolean,
  declined: boolean,
};

const noop = () => {};

const PIIDialog = (props: any) => (
  <Dialog
    size="small"
    hideCloseButton
    hideOnBlur={false}
    hideOnEscape={false}
    onRequestHide={noop}
    eventKey={props.eventKey}
    className="PIIConsentModal">
    {props.children}
  </Dialog>
);

async function wrapOperation(before, operation, after) {
  let result;
  let error;

  before();

  try {
    result = await operation();
  } catch (e) {
    error = e;
  }

  after();

  if (error) {
    throw error;
  } else {
    return result;
  }
}

class PIIConsentModal extends React.Component<Props, State> {
  state: State = {
    working: false,
    declined: false,
  };

  componentDidMount = () => {
    this.work(this.props._recordPrompt).then(undefined, error => {
      console.debug(error);
      console.error('PIIConsentModal - could not record prompt');
    });
  };

  work = (operation: *) => {
    return wrapOperation(
      () => this.setState({ working: true }),
      () => operation(),
      () => this.setState({ working: false })
    );
  };

  handlePIIConsentRefusal = () => {
    this.work(() => UserActions.delete(undefined, 'pii_consent')).then(
      this.props.onConsentRefused,
      this.props.onConsentRefusedError
    );
  };

  handlePIIConsentApproved = () => {
    this.work(this.props._recordAcceptance).then(
      this.props.onConsentGiven,
      this.props.onConsentGivenError
    );
  };

  render() {
    if (this.state.working) {
      return (
        <PIIDialog key="working">
          <div className="PIIConsentModal-working">
            <LoadingIndicator size="large" />
          </div>
        </PIIDialog>
      );
    }

    if (this.state.declined) {
      return (
        <PIIDialog key="prompt" eventKey="user_onboarding.account_deletion_modal.view">
          <React.Fragment>
            <BoxHeader
              image={require('assets/images/school-bell.svg')}
              title="Your account will be deleted"
            />
            <BoxBody className="PIIConsentModal-body">
              <div>
                In order for you to send and receive messages, Remind needs to collect your
                information, including your name, your school, your phone number, and your email
                address.
              </div>
              <div>By declining, you won’t be able to create an account.</div>
              <div>
                <Buttons axis="x" full>
                  <Button
                    color="lava"
                    level="secondary"
                    onClick={() => this.setState({ declined: false })}
                    eventKey="user_onboarding.account_deletion_modal.cancel.click">
                    Go back
                  </Button>
                  <Button
                    color="lava"
                    level="primary"
                    onClick={this.handlePIIConsentRefusal}
                    eventKey="user_onboarding.account_deletion_modal.delete_account.click">
                    Delete account
                  </Button>
                </Buttons>
              </div>
            </BoxBody>
          </React.Fragment>
        </PIIDialog>
      );
    }

    return (
      <PIIDialog key="delete" eventKey="user_onboarding.pii_tos_consent_modal.view">
        <React.Fragment>
          <BoxHeader title="User agreement" image={require('assets/images/user-agreement.svg')} />
          <BoxBody className="PIIConsentModal-body">
            <div>
              By signing up, you agree to Remind’s{' '}
              <Link
                target="_blank"
                href={LinkConstants.TERMS_OF_SERVICE}
                eventKey="user_onboarding.pii_tos_consent_modal.terms_of_service.click">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                target="_blank"
                href={LinkConstants.PRIVACY_POLICY}
                eventKey="user_onboarding.pii_tos_consent_modal.privacy_policy.click">
                Privacy Policy
              </Link>
              .
            </div>
            <div>
              You also agree to Remind collecting information from you in order to provide our
              service. This information includes your name, your school, your email, and your phone
              number.{' '}
              <Link
                target="_blank"
                href={LinkConstants.DATA_PRACTICE}
                eventKey="user_onboarding.pii_tos_consent_modal.learn_more.click">
                Learn more
              </Link>
            </div>
            <div>
              <Buttons axis="x" full>
                <Button
                  level="secondary"
                  onClick={() => this.setState({ declined: true })}
                  eventKey="user_onboarding.pii_tos_consent_modal.decline.click">
                  Decline
                </Button>
                <Button
                  level="primary"
                  className="PIIConsentModal-accept"
                  onClick={this.handlePIIConsentApproved}
                  eventKey="user_onboarding.pii_tos_consent_modal.accept.click">
                  Accept
                </Button>
              </Buttons>
            </div>
          </BoxBody>
        </React.Fragment>
      </PIIDialog>
    );
  }
}

const withMutation = graphql(
  gql`
    mutation UpdateUserPIIConsent($status: PIIConsentStatus!) {
      updateUserPIIConsent(input: { status: $status }) {
        error {
          message
        }
      }
    }
  `,
  {
    props: ({ mutate }) => ({
      _recordPrompt: () => mutate({ variables: { status: 'prompted' } }),
      _recordAcceptance: () => mutate({ variables: { status: 'accepted' } }),
    }),
  }
);

export { PIIConsentModal };
export default withMutation(PIIConsentModal);
